import { useQuery } from "@tanstack/react-query";
import { getCompaction } from "@/services/multi-agent/laneV2/api";
import type { SessionCompaction } from "@/services/multi-agent/laneV2/types";

/**
 * A v2 session's live compaction, and whether this server has `/compact` at all.
 *
 * ONE READ ANSWERS BOTH. `GET /api/sessions/{id}/compaction` is the feature probe: a server without
 * the compact routes (or with them switched off) answers 404, and then there is no command and no
 * divider. No capability key is involved, so nothing here depends on the R0 key set.
 *
 * WHY A QUERY AND NOT THE SESSION STORE. The store is rebuilt from the read model on every attach,
 * and its fields are hand-listed in a dozen places; a compaction is one small fact two components
 * read (the composer's command, the transcript's divider). A shared query key gives both the same
 * value, survives a reload the same way (it is read again), and is set directly after a compact.
 *
 * Read only once the session has a turn on the server: before that the session may not be pinned
 * yet (the read would 404 and wrongly look like "no such feature"), and there is nothing to compact.
 */
export const compactionKey = (sessionId: string | null) => ["lane-v2", "compaction", sessionId ?? ""] as const;

export interface SessionCompactionState {
  /** The server has the compact routes and this is a session it can compact. */
  available: boolean;
  compaction: SessionCompaction | null;
}

export function useSessionCompaction(sessionId: string | null, enabled: boolean): SessionCompactionState {
  const query = useQuery({
    queryKey: compactionKey(sessionId),
    queryFn: () => getCompaction(sessionId as string),
    enabled: enabled && !!sessionId,
    // A 404 is the answer ("not on this server"), not a blip to retry.
    retry: false,
    // ...nor an error to show (the global handler toasted it on every session of a server with the routes off)
    meta: { silent: true },
    staleTime: 60_000,
  });
  return { available: query.isSuccess, compaction: query.data?.compaction ?? null };
}
